import React, { useState } from "react";
import "./style.css";

const ChangePlanModal = ({ isOpen, onClose, currentPlan, availablePlans, onChangePlan, subscriptionData }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isChanging, setIsChanging] = useState(false);

  if (!isOpen) return null;

  const currentBillingCycle = subscriptionData?.membership_type || "month";
  
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan) return;
    
    setIsChanging(true);
    try {
      await onChangePlan(selectedPlan);
      onClose();
    } catch (error) {
      console.error("Error changing plan:", error);
    } finally {
      setIsChanging(false);
    }
  };

  const getPlanPrice = (plan) => {
    const pricingDetails = plan?.pricingDetails || [];
    const targetInterval = currentBillingCycle === "month" ? "monthly" : "yearly";
    
    const price = pricingDetails.find(p => {
      const interval = p?.recurring?.interval;
      const isPrimary = p?.metadata?.price_type === "primary-users";
      return isPrimary && interval === targetInterval;
    });
    
    return price ? (price.unit_amount / 100) : 0;
  };

  const formatFeatures = (plan) => {
    const metadata = plan?.metadata || {};
    const features = metadata?.plan_features;
    
    if (Array.isArray(features)) {
      return features.map(f => typeof f === 'string' ? f.replace(/<\/?b>/g, '') : f);
    }
    return [];
  };

  const isCurrentPlan = (plan) => {
    return currentPlan?.name === plan?.name;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="change-plan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Plans</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          <div className="current-plan-indicator">
            <div className="plan-header">
              <span className="current-plan-label">Current Plan</span>
              <span className="plan-status-badge active">Active</span>
            </div>
            <h3 className="plan-name">{currentPlan?.name || "Aligned"}</h3>
            <p className="plan-billing">
              ${getPlanPrice(currentPlan).toFixed(2)}/yr · Billed {currentBillingCycle === "month" ? "monthly" : "annually"}
            </p>
            <p className="plan-renewal">
              Renews on {new Date(subscriptionData?.renewal_data).toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>

          <div className="available-plans-section">
            {availablePlans
              .filter(plan => plan?.metadata?.plan_type === "main")
              .map((plan, index) => {
                const price = getPlanPrice(plan);
                const features = formatFeatures(plan);
                const isCurrent = isCurrentPlan(plan);
                const isSelected = selectedPlan?.plan_id === plan?.plan_id;

                return (
                  <div
                    key={plan?.plan_id || index}
                    className={`plan-card ${isCurrent ? 'current' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => !isCurrent && handlePlanSelect(plan)}
                  >
                    <div className="plan-card-header">
                      <h4 className="plan-card-name">{plan?.name}</h4>
                      {plan?.metadata?.yearly_discount && currentBillingCycle === "year" && (
                        <span className="discount-badge">{plan.metadata.yearly_discount}% Savings</span>
                      )}
                    </div>
                    <div className="plan-card-price">
                      ${price.toFixed(2)}
                      <span className="price-period">/{currentBillingCycle === "month" ? "month" : "year"}</span>
                    </div>
                    <p className="plan-card-description">{plan?.description}</p>
                    
                    {features.length > 0 && (
                      <ul className="plan-features-list">
                        {features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="plan-feature-item">
                            <svg className="check-icon-small" width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {isCurrent && (
                      <div className="current-plan-overlay">Current Plan</div>
                    )}
                  </div>
                );
              })}
          </div>

          {selectedPlan && !isCurrentPlan(selectedPlan) && (
            <div className="plan-change-actions">
              <button 
                className="btn-change-plan"
                onClick={handleConfirmChange}
                disabled={isChanging}
              >
                {isChanging ? "Changing..." : `Change to ${selectedPlan.name}`}
              </button>
              <p className="change-plan-note">
                Your plan will change at the next billing cycle on {new Date(subscriptionData?.renewal_data).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePlanModal;
